import { NAMED_RESIDENTS } from "./content.ts";
import type { PedestrianGraph } from "../../world/pedestrianGraph.ts";
import { nearestSegmentPointOnPedestrianGraph } from "../../world/pedestrianGraph.ts";
import type {
  NamedResidentDefinition,
  ResidentPresentation,
  ResidentScheduleEntry,
} from "./types.ts";

export function scheduleEntryAt(
  resident: Readonly<NamedResidentDefinition>,
  hour: number,
): ResidentScheduleEntry {
  const normalized = ((hour % 24) + 24) % 24;
  return resident.schedule.find(
    (entry) => normalized >= entry.startHour && normalized < entry.endHour,
  ) ?? resident.schedule[0]!;
}

export function namedResidentPresentations(
  hour: number,
): readonly ResidentPresentation[] {
  return NAMED_RESIDENTS.map((resident) => {
    const entry = scheduleEntryAt(resident, hour);
    return {
      id: resident.id,
      name: resident.name,
      role: resident.role,
      position: { ...resident.anchors[entry.anchor] },
      yawRadians: 0,
      elderVariant: resident.elderVariant,
      appearanceArchetype: resident.appearanceArchetype,
      clothing: resident.clothing,
      appearance: resident.appearance,
      activity: entry.activity,
      locomotionSpeedMps: 0,
      named: true,
    };
  });
}

export interface NamedResidentLocomotionOptions {
  readonly maximumSnapDistanceM: number;
  readonly patrolRadiusM: number;
  readonly walkingSpeedMps: number;
}

function stableUnitInterval(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0) / 0xffff_ffff;
}

/**
 * Advances a loaded named resident along the source-derived route nearest to
 * its retained schedule anchor. The bounded patrol keeps interactions local,
 * while a missing route produces an idle presentation instead of a treadmill.
 */
export function withNamedResidentLocomotion(
  resident: Readonly<ResidentPresentation>,
  graph: Readonly<PedestrianGraph>,
  simulationTimeSeconds: number,
  options: Readonly<NamedResidentLocomotionOptions>,
): ResidentPresentation {
  if (resident.activity !== "walking" || options.walkingSpeedMps <= 0) {
    return { ...resident, locomotionSpeedMps: 0 };
  }
  const segment = nearestSegmentPointOnPedestrianGraph(
    graph,
    resident.position,
    options.maximumSnapDistanceM,
  );
  if (segment === null || segment.lengthM <= 1e-3) {
    return { ...resident, locomotionSpeedMps: 0 };
  }

  const anchorDistanceM = segment.alpha * segment.lengthM;
  const startDistanceM = Math.max(
    0,
    anchorDistanceM - options.patrolRadiusM,
  );
  const endDistanceM = Math.min(
    segment.lengthM,
    anchorDistanceM + options.patrolRadiusM,
  );
  const patrolLengthM = endDistanceM - startDistanceM;
  if (patrolLengthM <= 1e-3) {
    return { ...resident, locomotionSpeedMps: 0 };
  }

  const cycleDistanceM = patrolLengthM * 2;
  const phaseOffsetM = stableUnitInterval(`${resident.id}:walking`) *
    cycleDistanceM;
  const phaseDistanceM = (
    simulationTimeSeconds * options.walkingSpeedMps + phaseOffsetM
  ) % cycleDistanceM;
  const returning = phaseDistanceM > patrolLengthM;
  const patrolDistanceM = returning
    ? cycleDistanceM - phaseDistanceM
    : phaseDistanceM;
  const distanceM = startDistanceM + patrolDistanceM;
  const alpha = distanceM / segment.lengthM;
  const direction = returning ? -1 : 1;
  const deltaX = segment.to.x - segment.from.x;
  const deltaZ = segment.to.z - segment.from.z;
  return {
    ...resident,
    position: {
      ...resident.position,
      x: segment.from.x + deltaX * alpha,
      z: segment.from.z + deltaZ * alpha,
    },
    yawRadians: Math.atan2(deltaX * direction, deltaZ * direction),
    locomotionSpeedMps: options.walkingSpeedMps,
  };
}

export function nearbyResidentPresentations(
  presentations: readonly Readonly<ResidentPresentation>[],
  focus: Readonly<{ x: number; z: number }>,
  radiusM: number,
): readonly ResidentPresentation[] {
  return presentations.filter((resident) =>
    Math.hypot(
      resident.position.x - focus.x,
      resident.position.z - focus.z,
    ) <= radiusM
  );
}

/**
 * Retains already-presented named residents until they clear a wider despawn
 * radius. A retained resident also keeps its loaded schedule anchor, so a
 * clock boundary cannot teleport an on-screen character to another anchor.
 */
export function stableNearbyResidentPresentations(
  scheduled: readonly Readonly<ResidentPresentation>[],
  retained: readonly Readonly<ResidentPresentation>[],
  focus: Readonly<{ x: number; z: number }>,
  spawnRadiusM: number,
  despawnRadiusM: number,
): readonly ResidentPresentation[] {
  if (despawnRadiusM < spawnRadiusM) {
    throw new RangeError("Resident despawn radius must cover spawn radius");
  }
  const scheduledIds = new Set(scheduled.map((resident) => resident.id));
  const retainedById = new Map(
    retained
      .filter((resident) =>
        scheduledIds.has(resident.id) &&
        Math.hypot(
            resident.position.x - focus.x,
            resident.position.z - focus.z,
          ) <= despawnRadiusM
      )
      .map((resident) => [resident.id, resident]),
  );
  return scheduled.flatMap((resident) => {
    const previous = retainedById.get(resident.id);
    if (previous !== undefined) {
      return [previous];
    }
    return Math.hypot(
        resident.position.x - focus.x,
        resident.position.z - focus.z,
      ) <= spawnRadiusM
      ? [resident]
      : [];
  });
}
